import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Map from '../components/Map';
import Logo from '../components/Logo';
import ExpiryTimer from '../components/ExpiryTimer';
import { decryptLocationData, importKeyFromString } from '../lib/crypto';

function getClientId(): string {
  const key = 'wobisch-client-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

interface LocationData {
  lat: number;
  lng: number;
  expiresAt: number;
  isLive?: boolean;
  lastUpdate?: number;
}

export default function ViewLocation() {
  const { code } = useParams<{ code: string }>();
  const [location, setLocation] = useState<LocationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAndDecrypt() {
      try {
        // Get encryption key from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const keyString = params.get('key');

        if (!keyString) {
          setError('Missing encryption key');
          setLoading(false);
          return;
        }

        // Fetch encrypted location
        const apiBase = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiBase}/api/location/${code}`, {
          headers: { 'X-Client-Id': getClientId() },
        });

        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Location not found');
          setLoading(false);
          return;
        }

        const data = await response.json();

        // Decrypt location
        const key = await importKeyFromString(keyString);
        const decrypted = await decryptLocationData(data.encryptedData, key);

        setLocation({
          lat: decrypted.lat,
          lng: decrypted.lng,
          expiresAt: data.expiresAt,
          isLive: data.isLive,
          lastUpdate: data.lastUpdate,
        });
      } catch (err) {
        console.error('Failed to decrypt location:', err);
        setError('Unable to decrypt location');
      } finally {
        setLoading(false);
      }
    }

    fetchAndDecrypt();

    // Poll for updates if live tracking
    const interval = setInterval(() => {
      if (location?.isLive) {
        fetchAndDecrypt();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [code, location?.isLive]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center">
        <Logo />
        <p className="text-gray-400 mt-4">Loading location...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-4">
        <Logo />
        <div className="mt-8 text-center">
          <p className="text-wobisch-red text-xl mb-2">Error</p>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!location) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col">
      <header className="p-4 flex items-center justify-between">
        <Logo size="small" />
        {location.isLive && (
          <span className="bg-wobisch-red text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
            LIVE
          </span>
        )}
      </header>

      <div className="flex-1 relative">
        <Map lat={location.lat} lng={location.lng} />
      </div>

      <footer className="p-4 bg-[#1a1a1a]">
        <ExpiryTimer expiresAt={location.expiresAt} />
        {location.lastUpdate && (
          <p className="text-gray-500 text-sm text-center mt-2">
            Last updated: {new Date(location.lastUpdate).toLocaleTimeString()}
          </p>
        )}
      </footer>
    </div>
  );
}
